import { ApiResourcify } from 'components/sharpr/api/ApiResourcify';
import _ from 'lodash';

export default function AffiliationHubRoute($stateProvider) {
	'ngInject';
	$stateProvider.state('hub.loggedin.affiliationHub', {
		url: '/admin/account/hubs/:id',
		controllerAs: 'vm',
		controller: AffiliationHubCtrl,
		templateUrl: '/ng/hub/Account/Hub/AccountHubCtrl.html?' + window.BUILD,
		permission: ['manage_hubs'],
		resolve: {
			$title: _.constant('Edit Hub :: Account Admin'),
			client: ApiResourcify.endpoint('/clients/:id'),
			themes: ApiResourcify.endpoint('/affiliations/themes'),
			languages: ApiResourcify.endpoint('/nosql/languages'),
		},
	});
}

class AffiliationHubCtrl {
	constructor($window, client, themes, languages, Api, Identity, notifications, modal, Cfg) {
		'ngInject';
		// save injections
		this.$window = $window;
		if (!client.Client) {
			// not sure of the best practice here.. If /clients/:id returns no hub info, we send the user back to the hubs list
			// the return is there to prevent a bonk in the logic below that relies on client.Client containing data
			window.location.href = this.isSuperadmin ? '/hub#/admin/hubs' : '/hub#/admin/account/hubs';
			return;
		}
		this.processClient(client);
		this.themes = themes;
		this.languages = [{ code: '', label: '' }, ...languages];
		this.Api = Api;
		this.Cfg = Cfg;
		this.notifications = notifications;
		this.modal = modal;
		// setup
		this.submissionEmailAlreadySet = client.Client.inbox_user !== null ? true : false;
		this.baseUrl = window.BASE_URL;
		this.crossPostingEnabled = false;
		this.setLoginUrl();
		this.sortHubsBy = 'name';
		this.affiliatedHubs = [];
		this.affiliatedHubIds = [];
		this.getAffiliatedHubs();
		this.accountChoices = Identity.accountChoices();
		this.isSuperadmin = Identity.isSuperadmin();
		this.allowedHubs = [];
		this.getAllowedHubs();
		this.allowedHubIds = [];
		this.allowAllHubs = false;
		this.clientPreferences = {};
		this.getClientPreferences();
		this.mode = 'edit';
		this.languageLookup = {};
		languages.forEach(language => {
			this.languageLookup[language.code] = language.label;
		});
		this.decideEmailDomain();
	}

	processClient(client) {
		this.client = client.Client;
		if (/@.+$/.test(this.client.inbox_user)) {
			[this.client.inbox_user] = this.client.inbox_user.split('@');
		}
	}

	decideEmailDomain() {
		const isSpecialDomain = /^(sharprstag\.com|sharprua\.com|sharprci\.com|sharpr\.de|sbuxinsightsportal\.com)$/.test(
			window.location.hostname
		);
		// Note: at this time, even cultureforbreakfast.com and cspacepulse.com
		// have email submissions through sharpr.com
		this.emailDomain = isSpecialDomain ? window.location.hostname : 'sharpr.com';
	}

	openLanguageDialog() {
		const refreshLanguage = async () => {
			const client = await this.Api.get(`/clients/${this.client.id}`);
			this.client.language = client.Client.language;
		};
		this.modal
			.state('hub.loggedin.AffiliationLanguage', {
				params: { id: this.client.id },
				heading: 'Update Hub Language',
				width: 750,
				height: 500,
			})
			.then(refreshLanguage, refreshLanguage);
	}

	getClientPreferences() {
		this.Api.get('/config/preferences', {}, { hubs: this.client.id }).then(prefs => {
			this.clientPreferences = prefs;
			// comes in as a string; angular doesn't check checkbox if this is a 1 or "1"
			this.crossPostingEnabled = parseInt(this.clientPreferences['Features.cross_post'], 10) ? true : false;
		});
	}

	getAffiliatedHubs() {
		this.Api.get('/clients/search?affiliation_id=' + this.client.affiliation_id).then(affiliatedHubs => {
			affiliatedHubs.forEach(hub => {
				// hubs can't subscribe to their own tags
				if (hub.Client.id != this.client.id) {
					hub.Client.id = parseInt(hub.Client.id, 10);
					this.affiliatedHubs.push(hub.Client);
					this.affiliatedHubIds.push(hub.Client.id);
				}
			});
			// we want to automatically check allowAllHubs if all
			/// `affiliatedHubIds` are in `allowedHubIds`
			this.allowAllHubs =
				this.affiliatedHubIds.length == this.allowedHubIds.length &&
				this.affiliatedHubIds.every(affiliatedHubId => {
					// is every `affiliatedHubId` somewhere in `this.allowedHubIds`?
					return this.allowedHubIds.indexOf(affiliatedHubId) > -1;
				});
		});
	}

	getAllowedHubs() {
		this.Api.get('/cross_post/allowed_hubs?hub_id=' + this.client.id).then(allowedHubs => {
			this.allowedHubIds = allowedHubs.map(hub => hub.id);
		});
	}

	updateCrossPostingEnabled(evt) {
		// is it enabled after the user has clicked the checkbox?
		var nowEnabled = evt.target.checked;
		if (!nowEnabled) {
			// selectAll not selected
			this.allowAllHubs = false;
		}

		this.Api.patch('/config/set', {
			code: 'Features.cross_post',
			value: nowEnabled ? '1' : '0',
			scope: 'hub',
			hub: this.client.id,
		});
	}

	updateAllowAllHubs() {
		if (this.allowAllHubs) {
			this.allowedHubIds = _.cloneDeep(this.affiliatedHubIds);
		} else {
			this.allowedHubIds = [];
		}
	}

	updateAllowedHubs(evt, hubId) {
		if (!evt.target.checked) {
			var hubIdIndex = this.allowedHubIds.indexOf(hubId);
			// it's now unchecked -- delete it from the list
			this.allowedHubIds.splice(hubIdIndex, 1);
			// if they are unchecking, then they don't want all hubs
			if (this.allowAllHubs === true) {
				this.allowAllHubs = false;
			}
		} else {
			// it's now checked -- add it to the list
			this.allowedHubIds.push(hubId);
		}
	}

	setLoginUrl() {
		let currentTheme = _.first(this.themes.filter(theme => Number(theme.id) === Number(this.client.client_theme_id)));
		this.loginUrl = currentTheme && currentTheme.login_url ? currentTheme.login_url : '';
	}

	setSubmissionEmail() {
		if (!this.submissionEmailAlreadySet) {
			this.client.inbox_user = (this.client.name + '').toLowerCase().replace(/[^\w.-]/g, '');
		}
	}

	saveSelectedHubs() {
		// if crossPostingEnabled is false, `this.allowedHubIds` will have been set to `[]`
		var url = '/cross_post/update_allowed_hubs/' + this.client.id;
		var data = {
			allowed_hub_ids: this.allowedHubIds,
		};
		this.Api.patch(url, data);
	}

	saveHub() {
		let url = '/clients/' + this.client.id;
		let data = {};
		data.name = this.client.name;
		data.inbox_user = this.client.inbox_user + '@' + this.emailDomain;
		if (this.client.client_theme_id) {
			data.client_theme_id = this.client.client_theme_id;
		} else {
			data.client_theme_id = 0; // Setting this to zero will put a null value in the DB.
		}
		this.Api.patch(url, data).then(() => {
			this.saveSelectedHubs();
			this.notifications.showSuccess({ message: 'Hub saved' });
			this.$window.location.href = this.isSuperadmin ? '/hub#/admin/hubs' : '/hub#/admin/account/hubs';
		});
	}

	deleteHub() {
		this.modal.confirm({ text: 'Are you sure you want to delete this hub?' }).then(() => {
			let url = '/clients/' + this.client.id;
			let data = {
				is_active: false,
			};
			this.Api.patch(url, data).then(() => {
				this.notifications.showSuccess({ message: 'Hub deleted' });
				this.$window.location.href = this.isSuperadmin ? '/hub#/admin/hubs' : '/hub#/admin/account/hubs';
			});
		});
	}
}
